import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Modal, Space, Spin, Tag, Typography, message } from "antd";

import { ancanaApi } from "../../../api/ancanaApi";
import { photoForm } from "../../../api/presentationsApi";
import CopyFieldsModal from "../copy/CopyFieldsModal";
import { ManageGalleriesLink, UploadTile, keepOffered } from "../gallery/PhotoPool";

/**
 * A development's brochure slide: its text, its two photos, and whether it
 * prints yet.
 *
 * Written once here and printed in every one of the development's listings'
 * brochures. Until everything the slide needs is here — per language — the
 * slide is simply left out of those brochures; nothing is blocked. The photos
 * come from the development's own galleries, above, or from its listings'.
 *
 * The Development group of the pool opens with an Upload tile: photos go to the
 * development's Brochure gallery and the first lands in the selected box.
 */

interface PhotoSlot {
  slot: string;
  label: string;
  aspect: number | null;
}

interface Candidate {
  ref: string;
  /** "Development", or the name of the listing the photo belongs to. */
  source: string;
  gallery: string;
  name: string;
  url: string;
  thumbnail: string;
}

export interface IDevelopmentSlide {
  template: number | null;
  slots: PhotoSlot[];
  candidates: Candidate[];
  picks: Record<string, string>;
  /** Per language: what is still missing before the slide prints. */
  missing: Record<string, string[]>;
}

const BOX_HEIGHT = 110;

const Body = styled.div`
  .status { margin-bottom: 12px; }
  .slots { display: flex; gap: 12px; margin: 8px 0; }
  .slot {
    position: relative; height: ${BOX_HEIGHT}px; padding: 0; overflow: hidden;
    background: #f5f5f5; border: 2px solid transparent; border-radius: 2px; cursor: pointer;
  }
  .slot.selected { border-color: #1890ff; }
  .slot img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .slot .name {
    position: absolute; left: 4px; bottom: 4px; font-size: 11px; color: #fff;
    background: rgba(0, 0, 0, 0.55); padding: 0 4px; border-radius: 2px;
  }
  .source { color: rgba(0, 0, 0, 0.55); font-size: 12px; margin: 10px 0 4px; }
  .pool { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 6px; }
  .pool-head { display: flex; justify-content: space-between; align-items: baseline; }
  .photo {
    aspect-ratio: 1; padding: 0; overflow: hidden; cursor: pointer;
    border: 2px solid transparent; border-radius: 2px; background: #f5f5f5;
  }
  .photo.in-use { border-color: #1890ff; }
  .photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

export const slideUrl = (developmentId: number) => `presentations/development/${developmentId}/`;

/** The server's `source` for the development's own photos. */
const OWN_GROUP = "Development";

function DevelopmentSlidePanel({ developmentId, onChanged }: {
  developmentId: number;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<IDevelopmentSlide>();
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  /** The pool again, after the gallery editor; unsaved picks are kept. */
  const reload = useCallback(() => {
    ancanaApi.get<IDevelopmentSlide>(slideUrl(developmentId)).then(({ data: fresh }) => {
      setData(fresh);
      setPicks((current) => keepOffered(current, fresh.candidates, (ref) => ref));
    }).catch(() => message.error("Could not load the development slide."));
  }, [developmentId]);

  const load = useCallback(() => {
    ancanaApi.get<IDevelopmentSlide>(slideUrl(developmentId)).then(({ data: fresh }) => {
      setData(fresh);
      setPicks(fresh.picks);
      setSelected((current) => current ?? fresh.slots[0]?.slot);
    }).catch(() => message.error("Could not load the development slide."));
  }, [developmentId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Spin />;

  if (data.template === null) {
    return (
      <Alert type="info" showIcon
             message="No active template has a development slide yet." />
    );
  }

  const dirty = JSON.stringify(picks) !== JSON.stringify(data.picks);
  const byRef = Object.fromEntries(data.candidates.map((c) => [c.ref, c]));
  // In server order: the development's own photos first, then each listing's.
  // The Development group is always there, empty or not: uploads land in it.
  const groups: [string, Candidate[]][] = [[OWN_GROUP, []]];
  data.candidates.forEach((candidate) => {
    const last = groups[groups.length - 1];
    if (last[0] === candidate.source) last[1].push(candidate);
    else groups.push([candidate.source, [candidate]]);
  });

  const upload = async (files: File[]) => {
    setUploading(true);
    try {
      const { data: fresh } = await ancanaApi.post<IDevelopmentSlide & { uploaded: string[] }>(
        `${slideUrl(developmentId)}upload/`, photoForm(files),
        { headers: { "Content-Type": "multipart/form-data" } });
      setData(fresh);
      const [first] = fresh.uploaded;
      if (selected && first) setPicks((current) => ({ ...current, [selected]: first }));
    } catch (e: any) {
      message.error(e?.response?.data?.files ?? "Could not upload the photos.");
    } finally {
      setUploading(false);
    }
  };

  const savePicks = async () => {
    setSaving(true);
    try {
      const body: Record<string, string | null> = {};
      data.slots.forEach(({ slot }) => { body[slot] = picks[slot] ?? null; });
      const { data: fresh } = await ancanaApi.put<IDevelopmentSlide>(
        slideUrl(developmentId), { picks: body });
      setData(fresh);
      setPicks(fresh.picks);
      onChanged?.();
      message.success("Photos saved.");
    } catch (e: any) {
      message.error(e?.response?.data?.picks ?? "Could not save the photos.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Body>
      <div className="status">
        {Object.entries(data.missing).map(([language, missing]) => (
          <div key={language}>
            <Tag color={missing.length ? "warning" : "success"}>{language.toUpperCase()}</Tag>
            {missing.length
              ? `Left out of brochures until it has: ${missing.join(", ")}`
              : "Prints in every brochure of this development."}
          </div>
        ))}
      </div>

      <Space style={{ marginBottom: 12 }}>
        <CopyFieldsModal developmentId={developmentId} listingName="development slide"
                         onSaved={() => { load(); onChanged?.(); }}>
          <Button>Edit slide text</Button>
        </CopyFieldsModal>
      </Space>

      <br />
      <br />

      <div className="pool-head">
        <Typography.Text type="secondary">Photos — select a box, then a photo.</Typography.Text>
        <ManageGalleriesLink recordPath="housing-developments" recordId={developmentId}
                             recordName="Development" onClose={reload} />
      </div>
      <div className="slots">
        {data.slots.map(({ slot, label, aspect }) => {
          const photo = picks[slot] ? byRef[picks[slot]] : undefined;
          return (
            <button key={slot} type="button" aria-label={slot}
                    className={`slot ${selected === slot ? "selected" : ""}`}
                    style={{ width: BOX_HEIGHT * (aspect ?? 1.5) }}
                    onClick={() => setSelected(slot)}>
              {photo && <img src={photo.thumbnail} alt="" />}
              <span className="name">{label}</span>
            </button>
          );
        })}
      </div>
      {groups.map(([source, candidates]) => (
        <div key={source}>
          <div className="source">{source}</div>
          <div className="pool">
            {source === OWN_GROUP && <UploadTile uploading={uploading} onFiles={upload} />}
            {candidates.map((candidate) => (
              <button key={candidate.ref} type="button" aria-label={candidate.ref}
                      className={`photo ${Object.values(picks).includes(candidate.ref) ? "in-use" : ""}`}
                      onClick={() => selected && setPicks({ ...picks, [selected]: candidate.ref })}>
                <img src={candidate.thumbnail} alt={candidate.name} />
              </button>
            ))}
          </div>
        </div>
      ))}
      <Button type="primary" style={{ marginTop: 12 }} disabled={!dirty}
              loading={saving} onClick={savePicks}>
        Save photos
      </Button>
    </Body>
  );
}

/** The panel in a modal, for the pre-generation checklist. */
export function DevelopmentSlideModal({ developmentId, children, onChanged }: {
  developmentId: number;
  children: React.ReactNode;
  onChanged?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>
      <Modal visible={open} onCancel={() => setOpen(false)} footer={null}
             title="Development slide" width={720} destroyOnClose>
        <DevelopmentSlidePanel developmentId={developmentId} onChanged={onChanged} />
      </Modal>
    </>
  );
}

export default DevelopmentSlidePanel;
